import React, { useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError, {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  actions as siteActions,
  operations as siteOperations,
  selectors as siteSelectors,
} from 'store/siteManager'
import ImplementationApproachModal from './component'
import { ENTRY_ID, fieldNames, validationSchema } from './data'

export const ImplementationApproachModalContainer = ({
  clearError,
  flowLabel,
  handleCancel,
  handleClose,
  hasApiError,
  modals,
  sitePreferences,
  site,
  updateSitePreferences,
}) => {
  const isMountedRef = useRef(true)

  useEffect(() => {
    // Track mount status
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  if (!modals) return null

  const closeModal = async () => {
    clearError('sitePreferences')

    // Close modal - let Redux state updates handle data refresh
    // Removing fetchUserSites/fetchUserContext calls to avoid race conditions
    // where state reloads before saved preferences are reflected
    if (isMountedRef.current) {
      handleClose()
    }
  }

  const getInitialValues = preferences => {
    const initialValues = {
      programIsEnabled: {
        k5Digital: preferences.k5Digital.programIsEnabled || false,
        mspDigital: preferences.mspDigital.programIsEnabled || false,
      },
    }

    fieldNames.forEach(field => {
      initialValues[field] = preferences[field] || null
    })

    return initialValues
  }

  const handleSave = async (values, isComplete) => {
    const newSitePreferences = {
      k5Digital: {
        programIsEnabled: values.programIsEnabled.k5Digital,
      },
      mspDigital: {
        programIsEnabled: values.programIsEnabled.mspDigital,
      },
    }

    fieldNames.forEach(field => {
      newSitePreferences.k5Digital[field] = values[field]
      newSitePreferences.mspDigital[field] = values[field]
      // Set *IsCompleted flags when saving finalized values (not draft)
      if (isComplete) {
        const isCompletedField = `${field}IsCompleted`
        newSitePreferences.k5Digital[isCompletedField] = true
        newSitePreferences.mspDigital[isCompletedField] = true
      }
    })

    // Set programIsEnabledIsCompleted when finalized
    if (isComplete) {
      newSitePreferences.k5Digital.programIsEnabledIsCompleted = true
      newSitePreferences.mspDigital.programIsEnabledIsCompleted = true
    }

    await updateSitePreferences(site.id, newSitePreferences)
  }

  const modalContent = modals[0]

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(sitePreferences)}
      validationSchema={validationSchema}
    >
      {({
        errors,
        isSubmitting,
        setFieldTouched,
        setFieldValue,
        setSubmitting,
        setTouched,
        touched,
        values,
      }) => (
        <ImplementationApproachModal
          choiceContent={modalContent.choiceContent}
          errors={errors}
          flowLabel={flowLabel}
          handleCancel={handleCancel}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleClose={handleClose}
          handleNextButtonClick={async () => {
            const isValidSync = validationSchema.isValidSync(values)
            if (!isValidSync) {
              const touchedFields = fieldNames.reduce((acc, name) => {
                acc[name] = true
                return acc
              }, {})
              touchedFields.programIsEnabled = {
                k5Digital: true,
                mspDigital: true,
              }
              await setTouched(touchedFields)
              return
            }
            setSubmitting(true)
            // ImplementationApproachModal is single-step, so always save as finalized
            await handleSave(values, true)
            setSubmitting(false)
            await closeModal()
          }}
          hasApiError={hasApiError}
          header={modalContent.header}
          helpCopy={modalContent.helpCopy}
          isSubmitting={isSubmitting}
          modals={modals}
          nextLabel={modalContent.nextLabel}
          setSubmitting={setSubmitting}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  )
}

ImplementationApproachModalContainer.propTypes = {
  clearError: PropTypes.func.isRequired,
  flowLabel: PropTypes.string.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  hasApiError: PropTypes.bool.isRequired,
  modals: PropTypes.array.isRequired,
  site: PropTypes.object.isRequired,
  sitePreferences: PropTypes.object.isRequired,
  updateSitePreferences: PropTypes.func.isRequired,
}

const mapStateToProps = (state, ownProps) => {
  const site = siteSelectors.selectSiteById(state, ownProps.siteId)
  const { sitePreferences = {} } = site || {}
  const { sitePreferences: sitePreferencesAPIResults } = state.siteManager
  const { error } = sitePreferencesAPIResults
  return {
    hasApiError: !!error,
    site,
    sitePreferences: {
      ...sitePreferences?.k5Digital,
      k5Digital: {
        programIsEnabled: sitePreferences?.k5Digital?.programIsEnabled,
      },
      mspDigital: {
        programIsEnabled: sitePreferences?.mspDigital?.programIsEnabled,
      },
    },
  }
}

const mapDispatchToProps = {
  clearError: siteActions.clearError,
  updateSitePreferences: siteOperations.updateSitePreferences,
}

const mapper = entry => {
  const { flowLabel, modals } = entry || {}
  return {
    flowLabel,
    modals,
  }
}

const options = {
  entryId: ENTRY_ID,
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    ImplementationApproachModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
